<script setup>
import { Mathematics } from '@tiptap-pro/extension-mathematics'
import { Node } from '@tiptap/core'
import { Blockquote } from '@tiptap/extension-blockquote'
import { Bold } from '@tiptap/extension-bold'
import { BulletList } from '@tiptap/extension-bullet-list'
import { Code } from '@tiptap/extension-code'
import { CodeBlock } from '@tiptap/extension-code-block'
import { Document } from '@tiptap/extension-document'
import { Dropcursor } from '@tiptap/extension-dropcursor'
import { Gapcursor } from '@tiptap/extension-gapcursor'
import { Heading } from '@tiptap/extension-heading'
import { History } from '@tiptap/extension-history'
import { HorizontalRule } from '@tiptap/extension-horizontal-rule'
import { Image } from '@tiptap/extension-image'
import { Italic } from '@tiptap/extension-italic'
import { Link } from '@tiptap/extension-link'
import { ListItem } from '@tiptap/extension-list-item'
import { OrderedList } from '@tiptap/extension-ordered-list'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Placeholder } from '@tiptap/extension-placeholder'
import { Strike } from '@tiptap/extension-strike'
import { Subscript } from '@tiptap/extension-subscript'
import { Superscript } from '@tiptap/extension-superscript'
import { Text } from '@tiptap/extension-text'
import { TextAlign } from '@tiptap/extension-text-align'
import { Underline } from '@tiptap/extension-underline'
import { Youtube } from '@tiptap/extension-youtube'
import { EditorContent, useEditor } from '@tiptap/vue-3'

//import { Table } from '@tiptap/extension-table'
//import { TableCell } from '@tiptap/extension-table-cell'
//import { TableHeader } from '@tiptap/extension-table-header'
//import { TableRow } from '@tiptap/extension-table-row'
import 'katex/dist/katex.min.css'


const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    required: false,
  },
})

const emit = defineEmits(['update:modelValue'])

defineOptions({
  name: 'TiptapPrinter',
  inheritAttrs: false,
})

const iframeHelper = Node.create({
  name: "iframe",
  group: "block",
  addOptions() {
    return {
      allowFullscreen: true,
      HTMLAttributes: {
        class: "old-iframe",
      },
      height: 480,
      width: 640,
    }
  },
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      frameborder: { default: 0 },
      allowfullscreen: { default: this.options.allowFullscreen, parseHTML: () => this.options.allowFullscreen },
      width: { default: this.options.width },
      height: { default: this.options.height },
    }
  },
  parseHTML() {
    return [{ tag: "iframe[class]" }]
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", this.options.HTMLAttributes, ["iframe", HTMLAttributes]]
  },
})

const refInputEl = ref(null)
const editorRef = ref(null)
const editorField = ref(null)

const elementId = computed(() => {
  const attrs = useAttrs()
  const _elementIdToken = attrs.id || attrs.label
  
  return _elementIdToken ? `app-text-field-${ _elementIdToken }-${ Math.random().toString(36).slice(2, 7) }` : undefined
})

const label = computed(() => useAttrs().label)

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    Document,
    iframeHelper,
    Paragraph.configure({ HTMLAttributes: { class: 'paragraph-editable' } }),
    Text,
    History,
    Dropcursor.configure({ class: 'dropcursor-editable' }),
    Gapcursor,
    Mathematics.configure({ katexOptions: { maxSize: 300 } }),    
    Placeholder.configure({ placeholder: props.placeholder ?? '' }),
    Bold,
    Underline,
    Italic,
    Strike,
    Blockquote,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right', 'justify'],
      defaultAlignment: 'left',
    }),
    BulletList,
    OrderedList,
    ListItem,
    HorizontalRule,
    CodeBlock,
    Code,
    Heading,
    Image.configure({ HTMLAttributes: { class: 'image-editable' }, inline: true }),
    Link.configure({ openOnClick: false }),
    Superscript,
    Subscript,
    Youtube.configure({ interfaceLanguage: 'es', enableIFrameApi: 'false', ccLanguage: 'es' }),

    /*Table.configure({
      resizable: true,
    }),
    TableRow,
    TableHeader,
    TableCell,*/
  ],
})

onMounted(()=>{
  editor.value?.setEditable(false)
})
</script>

<template>
  <div
    v-if="modelValue"
    class="tiptap"
  >
    <VLabel
      v-if="label"
      :for="elementId"
      class="mb-1 text-body-2 text-high-emphasis"
      :text="label"
    />
    <EditorContent
      ref="editorRef"
      :editor="editor"
      class="printer"
    />
  </div>
</template>

<style lang="scss">
  .printer{
    min-block-size: 5vh !important;
  }

.ProseMirror {
  padding: 0.5rem;
  min-block-size: 2vh;

  > * + * {
    margin-top: 0.75em;
  }


  ul,
  ol {
    padding: 0 2rem;
  }

  p {
    margin-block-end: 0;
  }

  p.is-editor-empty:first-child::before {
    block-size: 0;
    color: #adb5bd;
    content: attr(data-placeholder);
    float: inline-start;
    pointer-events: none;
  }
  a {
    color: #68CEF8;
    text-decoration: underline;
  }

  img {
    max-width: 50% !important;
  }
}


.image-editable{
  max-width: 80% !important;
  margin-left: 10%;
}

.editor{
  border: 1px solid rgba(118, 118, 118, 0.38);
  border-radius: 6px;
}

.tiptap:has(.editor +.v-input--error) .editor{
  border-color: rgba(234, 84, 85, 0.38)
}

.editorHelper > .v-input__control{
  display: none;
}

blockquote {
  padding-left: 1rem;
  border-left: 3px solid rgba(#0D0D0D, 0.1);
}

 pre {
    background: #0D0D0D;
    color: #FFF;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }
    code {
    font-size: 0.9rem;
    padding: 0.25em;
    border-radius: 0.25em;
    background-color: rgba(#616161, 0.1);
    color: #616161;
    box-decoration-break: clone;
  }


  /* Basic editor styles */
.tiptap {
  :first-child {
    margin-top: 0;
  }

  /* Table-specific styling */
  table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;

    td,
    th {
      border: 1px solid var(--gray-3);
      box-sizing: border-box;
      min-width: 1em;
      padding: 6px 8px;
      position: relative;
      vertical-align: top;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      background-color: var(--gray-1);
      font-weight: bold;
      text-align: left;
    }

    .selectedCell:after {
      background: var(--gray-2);
      content: "";
      left: 0; right: 0; top: 0; bottom: 0;
      pointer-events: none;
      position: absolute;
      z-index: 2;
    }

    .column-resize-handle {
      background-color: var(--purple);
      bottom: -2px;
      pointer-events: none;
      position: absolute;
      right: -2px;
      top: 0;
      width: 4px;
    }
  }

  .tableWrapper {
    margin: 1.5rem 0;
    overflow-x: auto;
  }

  &.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }
}
</style>
